import { useState, useEffect, useCallback, useMemo } from 'react';
import monthlyReportService from '../service/monthlyReportService.mjs';



export const useAuditorDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [pendingAuditClaims, setPendingAuditClaims] = useState([]);
  // Contractor claims waiting on the auditor's final approval.
  const [contractorClaims, setContractorClaims] = useState([]);
  const [contractorLoading, setContractorLoading] = useState(true);
  // Contractor claims already decided - approved (final) and sent back - shown
  // on the same tab, always (the user, 2026-10-21).
  const [contractorHistory, setContractorHistory] = useState({ approved: [], sentBack: [] });
  const [contractorHistoryLoading, setContractorHistoryLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pendingAuditLoading, setPendingAuditLoading] = useState(true);
  const [error, setError] = useState(null);
  // The server refused - no auditor access, or cover has lapsed.
  const [forbidden, setForbidden] = useState(false);
  const [pendingAuditForbidden, setPendingAuditForbidden] = useState(false);
  const [auditingClaimId, setAuditingClaimId] = useState(null);
  // Defaults kept in one place so "Clear filters" and the initial state can
  // never drift apart.
  const defaultFilters = () => ({
    region: 'all',
    year: new Date().getFullYear(),
    month: 'all',
    status: 'all'
  });

  const [filters, setFilters] = useState(defaultFilters);

  // Load monthly report dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await monthlyReportService.getAuditorDashboard();
      setDashboardData(data);
      setForbidden(false);
    } catch (err) {
      // A refusal is not an error to retry - it means this person has no
      // auditor access (or their cover has lapsed). Said plainly by the page.
      if (err.status === 403) setForbidden(true);
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load claims awaiting individual audit
  const loadPendingAuditClaims = useCallback(async () => {
    setPendingAuditLoading(true);
    try {
      const data = await monthlyReportService.getPendingAuditClaims();
      setPendingAuditClaims(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      // An empty list reads as "All Caught Up", which is a false statement to
      // someone the server refused. Record the refusal so the tab can say so.
      setPendingAuditClaims([]);
      setPendingAuditForbidden(err.status === 403);
    } finally {
      setPendingAuditLoading(false);
    }
  }, []);

  const loadContractorClaims = useCallback(async () => {
    setContractorLoading(true);
    try {
      const data = await monthlyReportService.getContractorClaims();
      setContractorClaims(Array.isArray(data?.data) ? data.data : []);
    } catch {
      // Shares the pre-approval tab's forbidden flag: both refuse for the same
      // reason - no auditor access, or cover that has lapsed.
      setContractorClaims([]);
    } finally {
      setContractorLoading(false);
    }
  }, []);

  const loadContractorHistory = useCallback(async () => {
    setContractorHistoryLoading(true);
    try {
      const data = await monthlyReportService.getContractorHistory();
      setContractorHistory({
        approved: Array.isArray(data?.approved) ? data.approved : [],
        sentBack: Array.isArray(data?.sentBack) ? data.sentBack : [],
        limit: data?.limit,
      });
    } catch {
      setContractorHistory({ approved: [], sentBack: [] });
    } finally {
      setContractorHistoryLoading(false);
    }
  }, []);

  // Individually audit a single claim, then remove it from the pending list.
  // `options.acknowledgeOpenQuestions` carries on past the open-questions warning.
  const auditClaim = useCallback(async (claimId, claimType, comments = '', options = {}) => {
    setAuditingClaimId(claimId);
    try {
      await monthlyReportService.auditClaim(claimId, claimType, comments, options);
      setPendingAuditClaims(prev => prev.filter(c => String(c.claimId) !== String(claimId)));
      // The same call finally approves a contractor's claim, so it leaves that
      // list too - one audit, whichever queue it was opened from.
      setContractorClaims(prev => prev.filter(c => String(c.claimId) !== String(claimId)));
      // A contractor's claim it finished now shows under Approved.
      loadContractorHistory();
    } finally {
      setAuditingClaimId(null);
    }
  }, [loadContractorHistory]);

  // Return a pending claim for revision (→ under_review), then remove it from
  // the pending list. Comment is required.
  const returnClaim = useCallback(async (claimId, claimType, comments) => {
    setAuditingClaimId(claimId);
    try {
      await monthlyReportService.returnClaim(claimId, claimType, comments);
      setPendingAuditClaims(prev => prev.filter(c => String(c.claimId) !== String(claimId)));
      // It left the contractor queue too - it stayed listed there until a
      // refresh - and now shows under Sent back.
      setContractorClaims(prev => prev.filter(c => String(c.claimId) !== String(claimId)));
      loadContractorHistory();
    } finally {
      setAuditingClaimId(null);
    }
  }, [loadContractorHistory]);

  // Filter monthly report data based on current filters
  const filteredData = useMemo(() => {
    return dashboardData.filter(item => {
      const matchesRegion = filters.region === 'all' || item.region === filters.region;
      const matchesYear = item.year === filters.year;
      const matchesMonth = filters.month === 'all' || item.month === parseInt(filters.month);
      const matchesStatus = filters.status === 'all' || item.status === filters.status;

      return matchesRegion && matchesYear && matchesMonth && matchesStatus;
    });
  }, [dashboardData, filters]);

  // Update filters
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters());
  }, []);

  // Get unique regions for filter dropdown
  const regions = useMemo(() => {
    const uniqueRegions = [...new Set(dashboardData.map(item => item.region))];
    return uniqueRegions.sort();
  }, [dashboardData]);

  // Get available years for filter dropdown
  const years = useMemo(() => {
    const uniqueYears = [...new Set(dashboardData.map(item => item.year))];
    return uniqueYears.sort((a, b) => b - a);
  }, [dashboardData]);

  // Get available months for filter dropdown
  const months = useMemo(() => {
    const uniqueMonths = [...new Set(dashboardData.map(item => item.month))];
    return uniqueMonths.sort((a, b) => a - b);
  }, [dashboardData]);

  // Refresh both data sets
  const refreshData = useCallback(() => {
    loadDashboardData();
    loadPendingAuditClaims();
    loadContractorClaims();
    loadContractorHistory();
  }, [loadDashboardData, loadPendingAuditClaims, loadContractorClaims, loadContractorHistory]);

  // Load both on mount
  useEffect(() => {
    loadDashboardData();
    loadPendingAuditClaims();
    loadContractorClaims();
    loadContractorHistory();
  }, [loadDashboardData, loadPendingAuditClaims, loadContractorClaims, loadContractorHistory]);

  return {
    dashboardData: filteredData,
    pendingAuditClaims,
    contractorClaims,
    contractorLoading,
    contractorHistory,
    contractorHistoryLoading,
    loading,
    pendingAuditLoading,
    error,
    forbidden,
    pendingAuditForbidden,
    auditingClaimId,
    filters,
    regions,
    years,
    months,
    updateFilter,
    clearFilters,
    refreshData,
    auditClaim,
    returnClaim,
    setError,
  };
};
